'use client';

import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Shield, Check } from 'lucide-react';

/**
 * TenantSelector - CR-005 Multi-Tenant Login Flow
 *
 * Displays tenant selection screen when user has multiple tenant memberships.
 * Shown after initial login when backend returns available_tenants array.
 *
 * Features:
 * - Card-based selection with radio buttons
 * - Visual indication of default tenant
 * - Shows role for each tenant
 * - "Remember my choice" checkbox
 * - Accessible keyboard navigation
 */

export interface TenantInfo {
  tenant_id: string;
  tenant_name: string;
  role: string;
  is_default: boolean;
}

interface TenantSelectorProps {
  tenants: TenantInfo[];
  onSelect: (tenantId: string, remember: boolean) => void;
  loading?: boolean;
}

export function TenantSelector({ tenants, onSelect, loading }: TenantSelectorProps) {
  // Default to the default tenant or first tenant
  const [selectedTenant, setSelectedTenant] = useState<string>(
    tenants.find(t => t.is_default)?.tenant_id || tenants[0]?.tenant_id || ''
  );
  const [remember, setRemember] = useState(false);

  const handleContinue = () => {
    if (selectedTenant) {
      onSelect(selectedTenant, remember);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, tenantId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedTenant(tenantId);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
      <Card className="w-full max-w-md p-6 shadow-lg">
        <div className="text-center mb-6">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-primary-600 dark:text-primary-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Select Organization
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            You have access to multiple organizations. Please select one to continue.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {tenants.map((tenant) => (
            <div
              key={tenant.tenant_id}
              className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedTenant === tenant.tenant_id
                  ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
              onClick={() => setSelectedTenant(tenant.tenant_id)}
              onKeyPress={(e) => handleKeyPress(e, tenant.tenant_id)}
              role="radio"
              aria-checked={selectedTenant === tenant.tenant_id}
              tabIndex={0}
            >
              <input
                type="radio"
                name="tenant"
                value={tenant.tenant_id}
                checked={selectedTenant === tenant.tenant_id}
                onChange={() => setSelectedTenant(tenant.tenant_id)}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1 ml-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {tenant.tenant_name}
                  </span>
                  {tenant.is_default && (
                    <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
                      Default
                    </span>
                  )}
                </div>
                <div className="flex items-center mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <Shield className="w-4 h-4 mr-1" />
                  <span>{tenant.role.replace('_', ' ').toUpperCase()}</span>
                </div>
              </div>
              {selectedTenant === tenant.tenant_id && (
                <Check className="absolute top-4 right-4 w-5 h-5 text-primary-600 dark:text-primary-400" />
              )}
            </div>
          ))}
        </div>

        <div className="mb-6">
          <label className="flex items-center text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
            />
            Remember my choice
          </label>
        </div>

        <Button
          onClick={handleContinue}
          disabled={!selectedTenant || loading}
          className="w-full"
        >
          {loading ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              Loading...
            </>
          ) : (
            'Continue'
          )}
        </Button>

        <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
          Need access to a different organization? Contact your administrator.
        </p>
      </Card>
    </div>
  );
}
